import { Injectable } from '@angular/core';
import { OperatorType } from 'angular-slickgrid';
import { PreAccessFilterValue } from '../models/pre-access-filter.models';
import { ColumnFilter, GridSnapshot } from '../../layout-list/interface/IGridSnapshot';

/**
 * Service responsible for merging pre-access filters with layout filters.
 * Pre-access filters take priority over layout filters when there's a conflict.
 */
@Injectable({
  providedIn: 'root',
})
export class PreAccessFilterMergeService {
  /**
   * Merge pre-access filters with layout filters.
   * Pre-access filters have priority - if a column has both AND pre-access has values, pre-access wins.
   * If pre-access filter has no values, the layout filter is used.
   *
   * @param preAccess Filters from the pre-access modal
   * @param layoutFilters Filters from the saved layout
   * @returns Merged array of column filters
   */
  mergeFilters(
    preAccess: PreAccessFilterValue[],
    layoutFilters: ColumnFilter[],
    priority: 'preaccess' | 'layout' = 'preaccess'
  ): ColumnFilter[] {
    // Convert pre-access filters to ColumnFilter format (only those with actual values)
    const preAccessAsColumnFilters = this.toColumnFilters(preAccess);

    if (priority === 'layout') {
      const activeLayoutMap = new Map(layoutFilters.map(f => [f.columnId, f]));
      const nonConflictingPreAccess = preAccessAsColumnFilters.filter(
        pf => !activeLayoutMap.has(pf.columnId)
      );
      return [...layoutFilters, ...nonConflictingPreAccess];
    }

    // Default: pre-access wins on conflicts
    const activePreAccessMap = new Map(preAccessAsColumnFilters.map(f => [f.columnId, f]));
    const nonConflicting = layoutFilters.filter(lf => !activePreAccessMap.has(lf.columnId));
    return [...preAccessAsColumnFilters, ...nonConflicting];
  }

  /**
   * Apply pre-access filters to a grid snapshot.
   * Returns a new snapshot with merged filters.
   *
   * @param snapshot The original grid snapshot
   * @param preAccess Pre-access filter values
   * @returns New snapshot with merged filters
   */
  applyToSnapshot(
    snapshot: GridSnapshot,
    preAccess: PreAccessFilterValue[],
    priority: 'preaccess' | 'layout' = 'preaccess'
  ): GridSnapshot {
    const mergedFilters = this.mergeFilters(preAccess, snapshot.filters || [], priority);

    return {
      ...snapshot,
      filters: mergedFilters,
    };
  }

  /**
   * Convert PreAccessFilterValue array to ColumnFilter array.
   * Handles operator conversion and format normalization.
   */
  toColumnFilters(preAccess: PreAccessFilterValue[]): ColumnFilter[] {
    return preAccess
      .filter(f => f.searchTerms && f.searchTerms.length > 0)
      .map(f => ({
        columnId: f.columnId,
        queryKey: f.queryKey,
        searchTerms: f.searchTerms,
        operator: this.mapOperator(f.operator),
      }));
  }

  /**
   * Map string operator to SlickGrid OperatorType
   */
  private mapOperator(operator: string): OperatorType {
    const operatorMap: Record<string, OperatorType> = {
      'EQ': OperatorType.equal,
      'NE': OperatorType.notEqual,
      'LT': OperatorType.lessThan,
      'LE': OperatorType.lessThanOrEqual,
      'GT': OperatorType.greaterThan,
      'GE': OperatorType.greaterThanOrEqual,
      'IN': OperatorType.in,
      'NIN': OperatorType.notIn,
      'Contains': OperatorType.contains,
      'StartsWith': OperatorType.startsWith,
      'EndsWith': OperatorType.endsWith,
      'RangeInclusive': OperatorType.rangeInclusive,
      'RangeExclusive': OperatorType.rangeExclusive,
    };

    return operatorMap[operator] || OperatorType.equal;
  }

  /**
   * Check if a pre-access filter array has any active filters
   */
  hasActiveFilters(preAccess: PreAccessFilterValue[]): boolean {
    return preAccess.some(f =>
      f.searchTerms &&
      f.searchTerms.length > 0 &&
      f.searchTerms.some(t => t !== null && t !== undefined)
    );
  }
}
